import { useEffect, useRef, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import CheckoutModal from "@/v2/components/checkout/CheckoutModal";
import { useV2Checkout } from "@/v2/hooks/useV2Checkout";
import FunnelLayout from "@/v2/components/FunnelLayout";
import GlassCard from "@/v2/components/GlassCard";
import PrimaryCTA from "@/v2/components/PrimaryCTA";
import { useAuth } from "@/contexts/AuthContext";
import { useV2Sprint } from "@/v2/hooks/useV2Sprint";
import SprintHeader from "@/v2/components/sprint/SprintHeader";
import SprintProgress from "@/v2/components/sprint/SprintProgress";
import SprintRoadmap from "@/v2/components/sprint/SprintRoadmap";
import SprintStepperCompact from "@/v2/components/sprint/SprintStepperCompact";
import SprintStepCard from "@/v2/components/sprint/SprintStepCard";
import RealityCheckSummary from "@/v2/components/sprint/RealityCheckSummary";
import CallInfoCard from "@/v2/components/sprint/CallInfoCard";
import SprintCompletePanel from "@/v2/components/sprint/SprintCompletePanel";
import { Button } from "@/components/ui/button";

export default function Sprint() {
  const { user, loading: authLoading } = useAuth();
  const {
    sprint,
    realityCheck,
    realityCheckMode,
    submissions,
    submitStep,
    loading,
    readOnly,
    isCompleted,
    isExpired,
    hasExpiry,
    daysRemaining,
    dayNumber,
    totalDays,
    completedCount,
    progressPct,
    currentStep,
  } = useV2Sprint();

  const [activeStep, setActiveStep] = useState<number>(1);
  const stepCardRef = useRef<HTMLDivElement>(null);
  const { busyTier, clientSecret, startCheckout, closeCheckout } = useV2Checkout();

  // maxCompleted monotono: protegge da refetch stale che potrebbe momentaneamente
  // restituire completed_steps vuoto e far ricomparire il banner "bloccato".
  const maxCompletedRef = useRef<number>(0);
  const sprintIdRef = useRef<string | null>(null);
  if (sprint && sprintIdRef.current !== sprint.id) {
    sprintIdRef.current = sprint.id;
    maxCompletedRef.current = sprint.completed_steps?.length
      ? Math.max(...sprint.completed_steps)
      : 0;
  }
  const rawCompleted = sprint?.completed_steps ?? [];
  const rawMaxCompleted = rawCompleted.length ? Math.max(...rawCompleted) : 0;
  if (rawMaxCompleted > maxCompletedRef.current) {
    maxCompletedRef.current = rawMaxCompleted;
  }
  const maxCompleted = maxCompletedRef.current;
  const completedSet = useMemo(() => {
    // Unione tra completed_steps reali e tutti gli step fino a maxCompleted
    // (monotono). Evita flicker di "step 1 non completato" su refetch stale.
    const s = new Set<number>(rawCompleted);
    for (let i = 1; i <= maxCompleted; i++) s.add(i);
    return s;
  }, [rawCompleted, maxCompleted]);
  const nextEditableStep = Math.min(5, maxCompleted + 1);

  const step3Submission = submissions.find((s) => s.step_number === 3);
  const isStep3Running =
    !completedSet.has(3) && !!step3Submission?.content?.test_launched_at;
  const runningStep: number | null = isStep3Running ? 3 : null;


  // Reset esplicito solo al cambio di sprint (nuovo sprint caricato).
  useEffect(() => {
    if (sprint) setActiveStep(nextEditableStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sprint?.id]);

  // Auto-advance monotono: se nextEditableStep cresce oltre activeStep, avanza.
  // Mai tornare indietro automaticamente (evita flicker durante refetch).
  useEffect(() => {
    setActiveStep((prev) => (nextEditableStep > prev ? nextEditableStep : prev));
  }, [nextEditableStep]);

  const visitedSteps = useMemo(
    () => Array.from(new Set(submissions.map((s) => s.step_number))).sort((a, b) => a - b),
    [submissions],
  );

  const seo = (
    <Helmet>
      <title>Il tuo Sprint | Proof | Progetto Startup</title>
      <meta name="description" content="Il tuo Sprint di 7 giorni è partito. Costruisci, porta traffico reale, raccogli dati. Verdetto basato su numeri concreti, non supposizioni." />
      <link rel="canonical" href="https://progettostartup.com/v2/sprint" />
      <meta property="og:title" content="Il tuo Sprint | Proof | Progetto Startup" />
      <meta property="og:description" content="Il tuo Sprint di 7 giorni è partito. Costruisci, porta traffico reale, raccogli dati. Verdetto basato su numeri concreti, non supposizioni." />
      <meta property="og:url" content="https://progettostartup.com/v2/sprint" />
      <meta name="robots" content="noindex" />
    </Helmet>
  );

  if (!authLoading && !user) {
    return <Navigate to="/" replace />;
  }

  if (authLoading || loading) {
    return (
      <FunnelLayout>
        {seo}
        <div className="max-w-3xl mx-auto">
          <GlassCard>
            <p className="text-sm text-slate-400">Caricamento…</p>
          </GlassCard>
        </div>
      </FunnelLayout>
    );
  }

  if (!sprint) {
    return (
      <FunnelLayout>
        {seo}

        <div className="max-w-2xl mx-auto space-y-6 text-center">
          <h1 className="text-3xl md:text-4xl font-bold">Nessuno Sprint attivo</h1>
          <p className="text-slate-300">
            Per iniziare un test reale di 7 giorni, scegli un piano. Se non hai ancora fatto il Reality Check, fallo prima: è gratuito.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <PrimaryCTA to="/v2/pricing" variant="lg">Inizia uno Sprint</PrimaryCTA>
            <PrimaryCTA to="/v2/reality-check" variant="outline">
              Fai prima il Reality Check
            </PrimaryCTA>
          </div>
        </div>
      </FunnelLayout>
    );
  }

  const currentSubmission = submissions.find((s) => s.step_number === activeStep);
  const isActiveStepCompleted = completedSet.has(activeStep);
  const isActiveStepVisited = visitedSteps.includes(activeStep);
  const gateBlocked =
    !readOnly &&
    !isActiveStepCompleted &&
    !isActiveStepVisited &&
    activeStep > nextEditableStep;
  const stepReadOnly = readOnly || gateBlocked;

  return (
    <FunnelLayout>
      {seo}
      <div className="max-w-5xl mx-auto space-y-5">
        <SprintHeader
          tier={sprint.tier}
          status={sprint.status}
          daysRemaining={daysRemaining}
          dayNumber={dayNumber}
          totalDays={totalDays}
          readOnly={readOnly}
          isCompleted={isCompleted}
          isExpired={isExpired}
          hasExpiry={hasExpiry}
        />

        <SprintProgress completed={completedCount} pct={progressPct} />

        <div className="grid gap-5 min-[960px]:grid-cols-[1fr_320px]">
          <div ref={stepCardRef} className="space-y-4 order-1 scroll-mt-20">
            {/* Compact stepper: visible only on mobile/tablet stretti (<960px) */}
            <div className="min-[960px]:hidden">
              <SprintStepperCompact
                completed={Array.from(completedSet).sort((a, b) => a - b)}
                current={nextEditableStep}
                active={activeStep}
                visited={visitedSteps}
                onSelect={setActiveStep}
                readOnly={readOnly}
                runningStep={runningStep}

              />
            </div>

            {gateBlocked ? (
              <GlassCard className="!p-5 md:!p-6 space-y-3">
                <p className="text-xs uppercase tracking-widest text-secondary">
                  Step {activeStep} di 5 · bloccato
                </p>
                <h2 className="text-xl md:text-2xl font-bold">
                  Completa prima lo Step {nextEditableStep}
                </h2>
                <p className="text-sm text-slate-300">
                  Lo Sprint è un protocollo: ogni step costruisce su quello precedente.
                  Per compilare lo Step {activeStep} devi prima completare lo Step {nextEditableStep}.
                </p>
                <div className="pt-1">
                  <Button
                    onClick={() => setActiveStep(nextEditableStep)}
                    className="bg-primary hover:bg-primary/90"
                  >
                    Vai allo Step {nextEditableStep}
                  </Button>
                </div>
              </GlassCard>
            ) : (
              <SprintStepCard
                key={activeStep}
                sprintId={sprint.id}
                step={activeStep}
                submission={currentSubmission}
                realityCheck={realityCheck}
                realityCheckMode={realityCheckMode}
                allSubmissions={submissions}
                readOnly={stepReadOnly}
                saving={submitStep.isPending}
                isFinalStep={activeStep === 5}
                isStepCompleted={isActiveStepCompleted}
                sprintStartedAt={sprint.started_at}
                onGoToStep={(n) => setActiveStep(n)}
                onSaveDraft={async (draft, opts) => {
                  await submitStep.mutateAsync({
                    stepNumber: activeStep,
                    content: draft.content,
                    evidenceLinks: draft.evidenceLinks,
                    completeStep: false,
                    unlockCall: !!opts?.unlockCall,
                  });
                }}
                onSubmit={async (draft) => {
                  const res = await submitStep.mutateAsync({
                    stepNumber: activeStep,
                    content: draft.content,
                    evidenceLinks: draft.evidenceLinks,
                    unlockCall: activeStep === 2,
                  });

                  // Auto-advance only on new completion (not edit, not final)
                  if (res && !res.editOnly && !res.completed && res.nextStep) {
                    setActiveStep(res.nextStep);
                    requestAnimationFrame(() => {
                      stepCardRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    });
                  }
                }}
              />

            )}

            {maxCompleted >= 5 && (
              <SprintCompletePanel
                submissions={submissions}
                finalVerdict={
                  submissions.find((s) => s.step_number === 5)?.content?.final_verdict
                }
              />
            )}
          </div>

          <aside className="space-y-4 order-2">
            {/* Roadmap dettagliata: solo desktop ≥960px */}
            <div className="hidden min-[960px]:block">
              <SprintRoadmap
                completed={Array.from(completedSet).sort((a, b) => a - b)}
                current={nextEditableStep}
                active={activeStep}
                visited={visitedSteps}
                onSelect={setActiveStep}
                readOnly={readOnly}
                runningStep={runningStep}

              />
            </div>
            <CallInfoCard tier={sprint.tier} callUnlockedAt={sprint.call_unlocked_at} />
            <RealityCheckSummary rc={realityCheck} />
            {(isExpired || isCompleted) && (
              <GlassCard className="!p-5">
                <p className="text-sm text-slate-300">
                  {isCompleted
                    ? "Hai completato il tuo Sprint. Vuoi testare un'altra ipotesi?"
                    : "Lo Sprint è scaduto. Puoi estenderlo di 7 giorni mantenendo tutti i dati raccolti, oppure iniziarne uno nuovo."}
                </p>
                <div className="mt-3 space-y-2">
                  {isExpired && (
                    <Button
                      onClick={() => startCheckout("extension_7_days")}
                      disabled={busyTier !== null}
                      className="w-full bg-primary hover:bg-primary/90"
                    >
                      {busyTier === "extension_7_days"
                        ? "Apertura…"
                        : "Estendi di 7 giorni · 19€"}
                    </Button>
                  )}
                  <PrimaryCTA to="/v2/pricing">Nuovo Sprint</PrimaryCTA>
                </div>
              </GlassCard>
            )}
          </aside>
        </div>
      </div>

      {clientSecret && (
        <CheckoutModal clientSecret={clientSecret} onClose={closeCheckout} />
      )}
    </FunnelLayout>
  );
}
